import NGNFlagImg from "asset/images/Currency.svg";
import GBPFlagImg from "asset/images/GB.svg";
import USFlagImg from "asset/images/US.png";
import EUFlagImg from "asset/images/EU.svg";
import KESFlagImg from "asset/icons/KES.svg";
import GHIcon from "asset/icons/GH.svg";
import { ArrowIcon, CheckCircleIcon } from "asset/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useGetRequest from "hooks/useGetRequest";
import currencyPairCheck from "../utilities/currencyPairCheck";
import { formatMoney } from "utilities/formatMoney";
import NewWalletModal from "./wallet/NewWalletModal";
import { useLocation, useNavigate } from "react-router-dom";
import WalletDetailsModal from "./wallet/WalletDetailsModal";
import WalletReadyModal from "./wallet/WalletReadyModal";
import USDCIcon from "asset/icons/USDC.svg";
import USDTIcon from "asset/icons/USDT.svg";
import { cn } from "lib/utils";
import { fetchKYSStatus } from "../api/fetchers";

const hasCompletedUpdateKyc = (stage) => Number(stage) >= 3;

const CurrencyToggle = ({
  businessId,
  fetchWallet,
  hideLabel,
  className = "",
  dropDownClassName = "",
  isDisabled = false,
  isTransparent = false,
  parentClassName = "",
  defaultCurrency,
  excludeProviderStatuses = [],
  includeCurrencies = [],
  forCreditWallet = false,
  newWalletsData,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isSendMoneyPage = location.pathname.includes("send-money");
  const isSendCryptoPage = location.pathname.includes("send-crypto");
  const mode = isSendMoneyPage ? "fiat-only" : isSendCryptoPage ? "crypto-only" : "all";

  const [kycStatus, setKycStatus] = useState("");
  const [kycStage, setKycStage] = useState(null);
  const [autoOpen, setAutoOpen] = useState(false);

  const state = useSelector((s) => s);
  const { wallets: storeWallets, currency: curr, businessSwitching, kycStatus: storeKycStatus } = state;
  const [fetchWallets, stateWallets, , , setStateWallets] = useGetRequest();

  const [isNewWalletOpen, setIsNewWalletOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const dispatch = useDispatch();
  const wrapperRef = useRef();
  const [modalShow, setModalShow] = useState(false);

  const cryptoAllowed = !isSendMoneyPage;

  const pickProvider = (w) => {
    const arr = Array.isArray(w?.providers) ? w.providers : [];
    return arr.find((p) => p?.accountNumber) || arr[0] || null;
  };

  const isCryptoCurrency = (c) => ["USDC", "USDT"].includes(String(c).toUpperCase());
  const allowedCurrencies = new Set(
    (includeCurrencies || []).map((code) => {
      if (typeof code === 'object') return String(code?.currency || "").toUpperCase();
      return String(code || "").toUpperCase();
    }).filter(Boolean)
  );

  const normalizeWallet = (w) => {
    if (!w) return null;
    const provider = pickProvider(w);
    return {
      _id: w._id,
      currency: w.currency,
      balance: Number(w.balance ?? 0),
      accountNumber: provider?.accountNumber || "",
      accountName: provider?.accountName || "",
      bankName: provider?.bankName || "",
      providerCurrency: provider?.currency || w.currency,
      asset: provider?.asset || null,
      type: w.type,
      providerStatus: provider?.status || "",
      hasProvider: !!provider,
      trackingReference: provider?.trackingReference || "",
    };
  };

  const requiresKycForCurrency = (code) => String(code).toUpperCase() === "USD";
  const isKycComplete = (s) =>
    ["complete", "completed", "verified", "verified"].includes(String(s).toLowerCase());

  const routeToKycStep = (stage = kycStage) => {
    const normalizedStage = Number(stage);

    if (normalizedStage === 2) {
      navigate("/update-kyc/additional-information");
      return;
    }

    navigate("/update-kyc/control-person");
  };

  const guardKycForCurrency = (code) => {
    if (!requiresKycForCurrency(code)) return false;
    if (isKycComplete(kycStatus) || hasCompletedUpdateKyc(kycStage)) return false;
    sessionStorage.setItem("postKycOpenCurrency", String(code).toUpperCase());
    routeToKycStep();
    return true;
  };

  const handleBeforeOpenWallet = (code) => {
    return guardKycForCurrency(code);
  };

  const fetchedWalletBusinessIdRef = useRef(null);
  const walletsFetchedForCurrentBusiness =
    fetchedWalletBusinessIdRef.current === businessId ? stateWallets : null;
  const rawWallets = (walletsFetchedForCurrentBusiness ?? storeWallets) || [];
  const allWallets = rawWallets
    .map(normalizeWallet)
    .filter(Boolean)
    .filter((w) => {
      if (w._id && w.currency) return true;
      if (isCryptoCurrency(w.currency)) return true;
      if (w.accountNumber) return true;
      if (w.hasProvider) return true;
      return false;
    });

  const wallets = allWallets.filter((w) =>
    mode === "fiat-only"
      ? !isCryptoCurrency(w.currency)
      : mode === "crypto-only"
        ? isCryptoCurrency(w.currency)
        : true
  );

  const _excluded = new Set((excludeProviderStatuses || []).map((s) => String(s).toLowerCase()));
  const visibleWallets = wallets.filter((w) => {
    const st = String(w.providerStatus || "").toLowerCase();
    const matchesInclude =
      allowedCurrencies.size === 0 ||
      allowedCurrencies.has(String(w.currency || "").toUpperCase());
    return !_excluded.has(st) && matchesInclude;
  });

  const setGlobalCurrency = (next) => {
    if (isDisabled) return;
    if (mode === "fiat-only" && isCryptoCurrency(next)) return;
    if (mode === "crypto-only" && !isCryptoCurrency(next)) return;
    if (fetchWallet) {
      const hasWallet = wallets.some((w) => w.currency === next);
      if (!hasWallet) return;
    }
    dispatch({ type: "CURRENCY", payload: next });
    localStorage.setItem("default", next);
  };

  const currency = defaultCurrency ? defaultCurrency : (curr ?? "NGN");
  const isDashboard = ["/dashboard", "/wallets"].includes(location.pathname);
  const visibleCurrencyKey = visibleWallets
    .map((w) => String(w.currency || "").toUpperCase())
    .join("|");
  const availableCurrencies = useMemo(
    () => visibleCurrencyKey.split("|").filter(Boolean),
    [visibleCurrencyKey]
  );
  const normalizedCurrency = String(currency || "").toUpperCase();
  const displayCurrency =
    !fetchWallet || defaultCurrency
      ? normalizedCurrency
      : businessSwitching
        ? ""
        : availableCurrencies.includes(normalizedCurrency)
          ? normalizedCurrency
          : availableCurrencies[0] || "";

  const currencyName = (c) =>
    c === "NGN"
      ? "Naira"
      : c === "EUR"
        ? "Euro"
        : c === "USD"
          ? "US Dollars"
          : c === "KES"
            ? "Kenyan shillings"
            : c === "GBP"
              ? "GB Pounds"
              : c === "USDT"
                ? "USDT"
                : c === "GHS"
                  ? "Ghana Cedi"
                  : c === "USDC"
                    ? "USDC"
                    : "Pound";

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && setModalShow(false);
    if (modalShow) document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [modalShow]);

  const normalizeKyc = (res) => {
    if (Array.isArray(res)) return { status: "", stage: null, data: res };

    const top = res && typeof res === "object" ? res : {};
    const inner = top && typeof top.data === "object" ? top.data : null;
    if (
      inner &&
      typeof inner === "object" &&
      !Array.isArray(inner) &&
      ("status" in inner || "stage" in inner || "data" in inner)
    ) {
      const { status = "", stage = null, data = [] } = inner;
      return { status, stage, data: Array.isArray(data) ? data : [] };
    }

    if ("status" in top || "stage" in top) {
      const { status = "", stage = null, data = [] } = top;
      return { status, stage, data: Array.isArray(data) ? data : [] };
    }

    if (Array.isArray(top.data)) return { status: "", stage: null, data: top.data };
    return { status: "", stage: null, data: [] };
  };

  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;

    (async () => {
      try {
        const raw = await fetchKYSStatus({ businessId });
        const { status, stage } = normalizeKyc(raw);

        if (cancelled) return;
        setKycStatus(status);
        const normalizedStatus = isKycComplete(status) ? "Completed" : "Incomplete";
        setKycStage(stage ?? null);
        dispatch({ type: "KYC_STATUS", payload: normalizedStatus });
      } catch (_) {}
    })();

    return () => {
      cancelled = true;
    };
  }, [businessId]);

  useEffect(() => {
    if (!kycStatus) return;

    const wanted = sessionStorage.getItem("postKycOpenCurrency");
    if (!wanted) return;
    if (wanted.toUpperCase() !== "USD") {
      sessionStorage.removeItem("postKycOpenCurrency");
      return;
    }

    if (isKycComplete(kycStatus) || hasCompletedUpdateKyc(kycStage)) {
      const hasUsd = wallets.some((w) => w.currency === "USD");
      if (!hasUsd) {
        setSelectedCurrency("USD");
        setAutoOpen(true);
      }
      sessionStorage.removeItem("postKycOpenCurrency");
    }
  }, [kycStatus, kycStage, wallets]);

  useEffect(() => {
    const onDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setModalShow(false);
    };
    if (modalShow) document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [modalShow]);

  useEffect(() => {
    if (!fetchWallet || !businessId || isDisabled) return;
    if (fetchedWalletBusinessIdRef.current === businessId) return;

    fetchedWalletBusinessIdRef.current = businessId;
    setStateWallets(null);
    fetchWallets("/v1/wallets/accounts", { headers: { "x-business-id": businessId } });
  }, [businessId, fetchWallet, fetchWallets, isDisabled, setStateWallets]);

  useEffect(() => {
    if (!curr) {
      const saved = localStorage.getItem("default") || "NGN";
      setGlobalCurrency(saved);
    }
  }, [curr]);

  useEffect(() => {
    if (!fetchWallet || defaultCurrency || isDisabled || !visibleWallets.length) return;

    const currentCurrency = String(currency || "").toUpperCase();
    if (availableCurrencies.includes(currentCurrency)) return;

    const nextCurrency = availableCurrencies[0];
    if (!nextCurrency) return;

    dispatch({ type: "CURRENCY", payload: nextCurrency });
    localStorage.setItem("default", nextCurrency);
  }, [
    currency,
    availableCurrencies,
    defaultCurrency,
    dispatch,
    fetchWallet,
    isDisabled,
    visibleCurrencyKey,
    visibleWallets.length,
  ]);

  useEffect(() => {
    if (!fetchWallet) return;
    if (Array.isArray(stateWallets)) {
      dispatch({ type: "WALLETS", payload: stateWallets });
    }
  }, [stateWallets, fetchWallet, dispatch]);

  function filterWalletsByCurrencies() {
    if (includeCurrencies.length > 0 && typeof includeCurrencies[0] === 'object') {
      return includeCurrencies;
    }
    return includeCurrencies.length
      ? newWalletsData?.filter((wallet) => allowedCurrencies.has(String(wallet.currency).toUpperCase()))
      : newWalletsData;
  }

  return (
    <div className={cn("relative inline-block", parentClassName)} ref={wrapperRef}>
      <NewWalletModal
        isOpen={isNewWalletOpen}
        onClose={() => setIsNewWalletOpen(false)}
        wallets={visibleWallets}
        onCurrencySelect={(code) => {
          setIsNewWalletOpen(false);
          setSelectedCurrency(code);
        }}
      />

      <WalletDetailsModal
        isOpen={!!selectedCurrency}
        currencyCode={selectedCurrency}
        autoOpen={autoOpen}
        onBeforeOpenWallet={handleBeforeOpenWallet}
        onClose={() => {
          setSelectedCurrency(null);
          setAutoOpen(false);
        }}
        onOpenWallet={(data) => {
          setSelectedCurrency(null);
          setAutoOpen(false);

          const code = String(data?.currency || selectedCurrency).toUpperCase();
          setWalletData({ currency: code, ...data });

          const exists = (storeWallets || []).some(
            (w) => String(w?.currency || "").toUpperCase() === code
          );
          if (!exists) {
            const optimisticWallet = {
              currency: code,
              balance: 0,
              providers: [
                {
                  accountName: data?.accountName || "",
                  accountNumber: data?.accountNumber || "",
                  bankName: data?.bankName || "",
                  status: data?.review ? "pending" : "active",
                },
              ],
              type: data?.walletType || "fiat",
            };
            dispatch({
              type: "WALLETS",
              payload: [...(storeWallets || []), optimisticWallet],
            });
          }

          if (fetchWallet && businessId) {
            fetchWallets("/v1/wallets/accounts", {
              headers: { "x-business-id": businessId },
            });
          }
        }}
      />

      <WalletReadyModal
        key={walletData?.currency || "wallet-ready"}
        isOpen={!!walletData}
        wallet={walletData}
        onClose={() => {
          setWalletData(null);
          if (fetchWallet && businessId) {
            fetchWallets("/v1/wallets/accounts", {
              headers: { "x-business-id": businessId },
            });
          }
          if (walletData?.currency) setGlobalCurrency(walletData.currency);
        }}
      />

      {modalShow && (
        <div
          className="fixed inset-0 z-[100] transition-opacity bg-black/5"
          onClick={() => setModalShow(false)}
        />
      )}

      <button
        className={cn(
          isTransparent ? "bg-[#EAECF0]" : "",
          `flex items-center rounded-[4px] p-1 max-375:text-sm 420:p-2 font-[400] relative
           ${className} ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`
        )}
        onClick={() =>
          !isDisabled && (wallets?.length || !fetchWallet) && setModalShow((p) => !p)
        }
        type="button"
        disabled={isDisabled}
      >
        {displayCurrency ? (
          <>
            <img
              src={
                displayCurrency === "NGN"
                  ? NGNFlagImg
                  : displayCurrency === "GBP"
                    ? GBPFlagImg
                    : displayCurrency === "EUR"
                      ? EUFlagImg
                      : displayCurrency === "USD"
                        ? USFlagImg
                        : displayCurrency === "KES"
                          ? KESFlagImg
                          : displayCurrency === "GHS"
                            ? GHIcon
                            : displayCurrency === "USDC"
                              ? USDCIcon
                              : displayCurrency === "USDT"
                                ? USDTIcon
                                : EUFlagImg
              }
              alt={displayCurrency}
              className="mr-1 size-5"
            />
            {displayCurrency}
            <ArrowIcon className={parentClassName ? "ml-auto" : "ml-1.5"} />
          </>
        ) : (
          <span
            className="inline-block h-5 w-20 rounded bg-[#EAECF0] animate-pulse"
            aria-label="Loading available currencies"
          />
        )}

        {!forCreditWallet ? (
          <div
            className={`absolute min-w-[320px] max-w-[373px] rounded-[16px] bg-white ${modalShow ? "pointer-events-auto" : "pointer-events-none"
              } -right-3 top-[48px] !py-5 transition-all duration-300 z-[250] ${dropDownClassName} ${modalShow
                ? "translate-y-0 opacity-100"
                : "translate-y-[50px] opacity-0 invisible"
              }`}
          >
            {!hideLabel && (
              <h3 className="!text-lg !font-medium text-left !-mt-1 !mb-3 pl-4">
                Switch wallets
              </h3>
            )}

            <div className="bg-white px-3 !pb-4 flex flex-col gap-2">
              {(fetchWallet
                ? visibleWallets
                : (cryptoAllowed
                  ? [
                    { _id: "ngn-fb", currency: "NGN" },
                    { _id: "gbp-fb", currency: "GBP" },
                    { _id: "eur-fb", currency: "EUR" },
                    { _id: "kes-fb", currency: "KES" },
                    { _id: "ghs-fb", currency: "GHS" },
                    { _id: "usd-fb", currency: "USD" },
                    { _id: "usdc-fb", currency: "USDC" },
                    { _id: "usdt-fb", currency: "USDT" },
                  ]
                  : [
                    { _id: "ngn-fb", currency: "NGN" },
                    { _id: "gbp-fb", currency: "GBP" },
                    { _id: "kes-fb", currency: "KES" },
                    { _id: "ghs-fb", currency: "GHS" },
                    { _id: "eur-fb", currency: "EUR" },
                    { _id: "usd-fb", currency: "USD" },
                  ]
                ).filter(
                  ({ currency: optionCurr }) =>
                    allowedCurrencies.size === 0 ||
                    allowedCurrencies.has(
                      String(optionCurr || "").toUpperCase()
                    )
                )
              )?.map(
                ({
                  _id,
                  currency: optionCurr,
                  balance = 0,
                  providerStatus,
                  accountNumber,
                }) => {
                  const isPendingWallet =
                    fetchWallet &&
                    !isCryptoCurrency(optionCurr) &&
                    providerStatus === "pending" &&
                    !accountNumber;

                  const onRowClick = (e) => {
                    e.stopPropagation();
                    if (isPendingWallet) return;
                    setGlobalCurrency(optionCurr);
                    setModalShow(false);
                  };

                  return (
                    <div
                      key={_id}
                      role="button"
                      aria-disabled={isPendingWallet}
                      tabIndex={isPendingWallet ? -1 : 0}
                      onKeyDown={(e) => {
                        if (isPendingWallet) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onRowClick(e);
                        }
                      }}
                      title={
                        isPendingWallet
                          ? `Your ${optionCurr} wallet is being set up. You’ll be able to use it once it’s ready.`
                          : undefined
                      }
                      className={cn(
                        "flex gap-2.5 py-2 px-2 rounded-lg items-center",
                        currency === optionCurr
                          ? "border-1 border-solid !border-[#6F00FF]"
                          : "border border-transparent",
                        isPendingWallet
                          ? "opacity-60 cursor-not-allowed select-none"
                          : "cursor-pointer hover:bg-[#F6F7FB]"
                      )}
                      onClick={onRowClick}
                    >
                      {currency === optionCurr ? (
                        <CheckCircleIcon className="size-5" />
                      ) : (
                        <CheckCircleIcon className="size-5 *:fill-[#DBDFE5]" />
                      )}

                      <img
                        alt={optionCurr}
                        src={
                          optionCurr === "NGN"
                            ? NGNFlagImg
                            : optionCurr === "GBP"
                              ? GBPFlagImg
                              : optionCurr === "EUR"
                                ? EUFlagImg
                                : optionCurr === "USD"
                                  ? USFlagImg
                                  : optionCurr === "KES"
                                    ? KESFlagImg
                                    : optionCurr === "GHS"
                                      ? GHIcon
                                      : optionCurr === "USDC"
                                        ? USDCIcon
                                        : optionCurr === "USDT"
                                          ? USDTIcon
                                          : EUFlagImg
                        }
                        className="rounded-full size-12"
                      />

                      <p className="font-semibold text-left">
                        {currencyName(optionCurr)}
                        <span className="block !mt-[3px] font-medium text-[rgb(152,162,179)]">
                          {currencyPairCheck(optionCurr)}{" "}
                          {fetchWallet && (
                            <span className="text-[rgb(102,112,133)]">
                              {formatMoney(balance.toFixed(2))}
                            </span>
                          )}
                        </span>
                      </p>

                      {isPendingWallet && (
                        <span className="ml-auto text-xs rounded-md px-2 py-0.5 bg-[#E1E3F4] text-[#475467]">
                          Pending
                        </span>
                      )}
                    </div>
                  );
                }
              )}
            </div>

            {isDashboard && (
              <div className="flex justify-end pt-2 pr-3 border-t">
                <button
                  className="bg-[#6F00FF] px-3 text-base rounded-lg py-2 text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsNewWalletOpen(true);
                    setModalShow(false);
                  }}
                >
                  <span className="pr-3">+</span> Add new
                </button>
              </div>
            )}
          </div>
        ) : (
          <div
              className={`absolute min-w-[320px] max-w-[373px] rounded-[16px] bg-white ${modalShow ? "pointer-events-auto" : "pointer-events-none"
                } -right-3 top-[48px] !py-5 transition-all overflow-y-auto duration-300 z-[250] ${dropDownClassName} ${modalShow
                  ? "translate-y-0 opacity-100"
                  : "translate-y-[50px] opacity-0 invisible"
                }`}
            >
              {!hideLabel && (
                <h3 className="!text-lg !font-medium text-left !-mt-1 !mb-3 pl-4">
                  Switch wallets
                </h3>
              )}

              <div className="bg-white px-3 !pb-4 flex flex-col gap-2">
                {filterWalletsByCurrencies()?.map(
                  ({
                    _id,
                    currency: optionCurr,
                    balance = 0,
                    providerStatus = "",
                    accountNumber,
                  }) => {
                    const isPendingUsd =
                      optionCurr === "USD" &&
                      (!accountNumber || providerStatus === "pending");

                  const onRowClick = (e) => {
                    e.stopPropagation();
                    if (isPendingUsd) return;
                    setGlobalCurrency(optionCurr);
                    setModalShow(false);
                  };

                  return (
                    <div
                      key={_id}
                      role="button"
                      aria-disabled={isPendingUsd}
                      tabIndex={isPendingUsd ? -1 : 0}
                      onKeyDown={(e) => {
                        if (isPendingUsd) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onRowClick(e);
                        }
                      }}
                      title={
                        isPendingUsd
                          ? "Your USD wallet is being set up. You’ll be able to use it once it’s ready."
                          : undefined
                      }
                      className={cn(
                        "flex gap-2.5 py-2 px-2 rounded-lg items-center",
                        currency === optionCurr
                          ? "border-1 border-solid !border-[#6F00FF]"
                          : "border border-transparent",
                        isPendingUsd
                          ? "opacity-60 cursor-not-allowed select-none"
                          : "cursor-pointer hover:bg-[#F6F7FB]"
                      )}
                      onClick={onRowClick}
                    >
                      {currency === optionCurr ? (
                        <CheckCircleIcon className="size-5" />
                      ) : (
                        <CheckCircleIcon className="size-5 *:fill-[#DBDFE5]" />
                      )}

                      <img
                        alt={optionCurr}
                        src={
                          optionCurr === "NGN"
                            ? NGNFlagImg
                            : optionCurr === "GBP"
                              ? GBPFlagImg
                              : optionCurr === "EUR"
                                ? EUFlagImg
                                : optionCurr === "USD"
                                  ? USFlagImg
                                  : optionCurr === "KES"
                                    ? KESFlagImg
                                    : optionCurr === "USDC"
                                      ? USDCIcon
                                      : optionCurr === "USDT"
                                        ? USDTIcon
                                        : EUFlagImg
                        }
                        className="rounded-full size-12"
                      />

                      <p className="font-semibold text-left">
                        {currencyName(optionCurr)}
                        <span className="block !mt-[3px] font-medium text-[rgb(152,162,179)]">
                          {currencyPairCheck(optionCurr)}{" "}
                          {fetchWallet && (
                            <span className="text-[rgb(102,112,133)]">
                              {formatMoney(balance.toFixed(2))}
                            </span>
                          )}
                        </span>
                      </p>

                      {isPendingUsd && (
                        <span className="ml-auto text-xs rounded-md px-2 py-0.5 bg-[#E1E3F4] text-[#475467]">
                          Pending
                        </span>
                      )}
                    </div>
                  );
                }
              )}
              </div>

            {isDashboard && (
              <div className="flex justify-end pt-2 pr-3 border-t">
                <button
                  className="bg-[#6F00FF] px-3 text-base rounded-lg py-2 text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsNewWalletOpen(true);
                    setModalShow(false);
                  }}
                >
                  <span className="pr-3">+</span> Add new
                </button>
              </div>
            )}
          </div>
        )}
      </button>
    </div>
  );
};

export default CurrencyToggle;
